import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngxs/store';
import { environment } from '@env/environment';
import { SetAccessToken } from '@app/state/sesion/sesion.actions';
import { LoginService } from './login.service';

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface RespuestaKpi { etiqueta: string; valor: string; delta?: string | null; }
export interface RespuestaTabla {
  titulo?: string | null;
  columnas: string[];
  filas: (string | number)[][];
  totalFilas?: number | null;
}
export interface RespuestaGrafico {
  tipo: 'bar' | 'line' | 'pie';
  titulo?: string | null;
  etiquetas: string[];
  series: { nombre: string; datos: number[] }[];
}
export interface RespuestaAccion { label: string; ruta: string; }
export interface Respuesta {
  texto: string;
  kpis?: RespuestaKpi[] | null;
  tabla?: RespuestaTabla | null;
  grafico?: RespuestaGrafico | null;
  acciones?: RespuestaAccion[] | null;
  sugerencias?: string[] | null;
}

export interface Conversacion { id: number; title: string | null; updatedAt: string; }
export interface MensajeGuardado { id: number; role: 'user' | 'assistant'; content: any; createdAt: string; }

export type AiChatEvent =
  | { type: 'tool'; name: string }
  | { type: 'respuesta'; data: Respuesta }
  | { type: 'text'; content: string }
  | { type: 'error'; message: string }
  | { type: 'meta'; conversationId: number }
  | { type: 'saved'; messageId: number };

/**
 * Cliente del servicio teki-ai. Usa fetch (no HttpClient) porque la
 * respuesta es un stream SSE; replica los headers del TokenInterceptor
 * (Authorization ya incluye "Bearer ", companyInfo es un JSON string).
 */
@Injectable({ providedIn: 'root' })
export class AiChatService {

  constructor(private loginService: LoginService, private store: Store, private http: HttpClient) { }

  async *chat(message: string, history: ChatTurn[], conversationId?: number | null, persist = true): AsyncGenerator<AiChatEvent> {
    const body = JSON.stringify({ message, history, conversationId: conversationId ?? undefined, persist });
    let response = await this.postChat(body);

    // El streaming usa fetch, fuera del interceptor que auto-refresca el token.
    // Si el token venció (401), refrescamos una vez y reintentamos, igual que el interceptor.
    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        response = await this.postChat(body);
      }
    }

    if (!response.ok || !response.body) {
      throw new Error(`El asistente respondió ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      let separatorIndex: number;
      while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        const parsed = this.parseSseEvent(rawEvent);
        if (parsed) {
          yield parsed;
        }
      }
    }
  }

  private postChat(body: string): Promise<Response> {
    return fetch(`${environment.iaEndpoint}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.loginService.getToken(),
        'companyInfo': this.loginService.getCompanyInfo()
      },
      body
    });
  }

  /** Refresca el access token (mismo flujo del interceptor) y lo guarda en el store. */
  private async refreshToken(): Promise<boolean> {
    try {
      const tokens: any = await firstValueFrom(this.loginService.refresh());
      if (tokens?.accessToken) {
        this.store.dispatch(new SetAccessToken(tokens.accessToken));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private parseSseEvent(raw: string): AiChatEvent | null {
    let eventName = '';
    let data = '';
    for (const line of raw.split('\n')) {
      if (line.startsWith('event: ')) {
        eventName = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      }
    }
    if (!eventName || eventName === 'done' || !data) {
      return null;
    }
    try {
      const obj: any = JSON.parse(data);
      if (obj && obj.type) {
        return obj as AiChatEvent;
      }
      if (eventName === 'meta') {
        return { type: 'meta', conversationId: obj.conversationId };
      }
      if (eventName === 'saved') {
        return { type: 'saved', messageId: obj.messageId };
      }
      return null;
    } catch {
      return null;
    }
  }

  /** Lista de conversaciones del usuario (vía HttpClient, pasa por el interceptor). */
  listConversations(): Promise<Conversacion[]> {
    return firstValueFrom(
      this.http.get<{ conversaciones: Conversacion[] }>(`${environment.iaEndpoint}/conversations`)
    ).then(r => r.conversaciones || []).catch(() => []);
  }

  getConversation(id: number): Promise<MensajeGuardado[]> {
    return firstValueFrom(
      this.http.get<{ mensajes: MensajeGuardado[] }>(`${environment.iaEndpoint}/conversations/${id}`)
    ).then(r => r.mensajes || []).catch(() => []);
  }

  sendFeedback(messageId: number, rating: 1 | -1): Promise<boolean> {
    return firstValueFrom(
      this.http.post<{ ok: boolean }>(`${environment.iaEndpoint}/conversations/feedback`, { messageId, rating })
    ).then(r => !!r.ok).catch(() => false);
  }
}
